import React from 'react';
import { i18n } from 'config/locales';
import Field from 'components/Form/Field';
import Textarea from 'components/Form/Textarea';
import DateRange from 'components/Form/DateRange';
import Branch from 'components/Form/Branch';
import Show from 'components/Form/Show';
import { LegalAssociationsEventItemModel } from 'forms/sections/legal/associations/eventItem/types';
import ValidationElement, {
  ValidationElementProps,
} from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnError,
  CommonOnUpdate,
  CommonRequired,
  CommonScrollIntoView,
} from '../../../../types/props';

interface Props
  extends Partial<LegalAssociationsEventItemModel>,
  ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
}

export default class EventItem extends ValidationElement<Props> {
  update = (queue: Partial<LegalAssociationsEventItemModel>) => {
    this.props.onUpdate({
      Was: this.props.Was,
      Why: this.props.Why,
      What: this.props.What,
      When: this.props.When,
      WhyStill: this.props.WhyStill,
      WhyStop: this.props.WhyStop,
      ...queue,
    });
  };

  updateWas = (values: LegalAssociationsEventItemModel['Was']) => {
    this.update({
      Was: values,
    });
  };

  updateWhy = (values: LegalAssociationsEventItemModel['Why']) => {
    this.update({
      Why: values,
    });
  };

  updateWhat = (values: LegalAssociationsEventItemModel['What']) => {
    this.update({
      What: values,
    });
  };

  updateWhen = (values: LegalAssociationsEventItemModel['When']) => {
    this.update({
      When: values,
    });
  };

  updateWhyStill = (values: LegalAssociationsEventItemModel['WhyStill']) => {
    this.update({
      WhyStill: values,
      WhyStop: {
        value: '',
        name: 'WhyStop',
      },
    });
  };

  updateWhyStop = (values: LegalAssociationsEventItemModel['WhyStop']) => {
    this.update({
      WhyStop: values,
      WhyStill: {
        value: '',
        name: 'WhyStop',
      },
    });
  };

  render() {
    const {
      subSectionAssetPath,
      subSectionClassName,
      hasWas,
      hasWhy,
      hasWhen,
      hasWhenFollowUp,
      hasWhat,
    } = this.props;

    const attribute = `${this.props.attribute}.items[${this.props.$index}].Item`;
    return (
      <div>
        {hasWas && (
          <Branch
            key={`${attribute}.Was`}
            name="Was"
            label={i18n.t(`${subSectionAssetPath}.Was`)}
            labelSize="h4"
            className={`${subSectionClassName}-Was`}
            {...this.props.Was}
            warning
            onError={this.handleError}
            required={this.props.required}
            onUpdate={this.updateWas}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Was`}
          />
        )}
        {hasWhy && (
          <Field
            key={`${attribute}.Why`}
            title={i18n.t(`${subSectionAssetPath}.Why`)}
            adjustFor="textarea"
            attribute={`${attribute}.Why`}
            scrollIntoView={this.props.scrollIntoView}
          >
            <Textarea
              name="Why"
              attribute={`${attribute}.Why`}
              {...this.props.Why}
              onUpdate={this.updateWhy}
              onError={this.props.onError}
              className={`${subSectionClassName}-Why`}
              required={this.props.required}
            />
          </Field>
        )}
        {hasWhen && (
          <Field
            key={`${attribute}.When`}
            title={i18n.t(`${subSectionAssetPath}.When`)}
            help={`${subSectionAssetPath}.help.when`}
            adjustFor="daterange"
            attribute={`${attribute}.When`}
            scrollIntoView={this.props.scrollIntoView}
          >
            <DateRange
              name="Dates"
              attribute={`${attribute}.When`}
              {...this.props.When}
              minDateEqualTo
              onUpdate={this.updateWhen}
              onError={this.props.onError}
              className={`${subSectionClassName}-When`}
              required={this.props.required}
              hideFromDay
              hideToDay
            />
            {hasWhenFollowUp && (
              <div>
                <Show when={this.props?.When?.present}>
                  <Textarea
                    name="WhyStill"
                    label={i18n.t(`${subSectionAssetPath}.WhyStill`)}
                    attribute={`${attribute}.WhyStill`}
                    {...this.props.WhyStill}
                    onUpdate={this.updateWhyStill}
                    onError={this.props.onError}
                    className={`${subSectionClassName}-whyStill`}
                    required={this.props.required}
                  />
                </Show>
                <Show when={!this.props?.When?.present}>
                  <Textarea
                    name="WhyStop"
                    label={i18n.t(`${subSectionAssetPath}.WhyStop`)}
                    attribute={`${attribute}.WhyStop`}
                    {...this.props.WhyStop}
                    onUpdate={this.updateWhyStop}
                    onError={this.props.onError}
                    className={`${subSectionClassName}-whyStop`}
                    required={this.props.required}
                  />
                </Show>
              </div>
            )}
          </Field>
        )}
        {hasWhat && (
          <Field
            key={`${attribute}.What`}
            title={i18n.t(`${subSectionAssetPath}.What`)}
            adjustFor="textarea"
            attribute={`${attribute}.What`}
            scrollIntoView={this.props.scrollIntoView}
          >
            <Textarea
              name="What"
              attribute={`${attribute}.What`}
              {...this.props.What}
              onUpdate={this.updateWhat}
              onError={this.props.onError}
              className={`${subSectionClassName}-What`}
              required={this.props.required}
            />
          </Field>
        )}
      </div>
    );
  }
}

EventItem.defaultProps = {
  Was: {},
  Why: {},
  When: {},
  WhyStill: {},
  WhyStop: {},
  What: {},
  hasWas: false,
  hasWhy: false,
  hasWhen: false,
  hasWhenFollowUp: false,
  hasWhat: false,
  questionLayout: [],
  onUpdate: (queue) => { },
  onError: (value, arr) => arr,
};
